/* Sentry initialisation for the React SPA.
 *
 * No-op when VITE_SENTRY_DSN is unset — the SDK isn't even loaded into
 * the runtime bundle in that case (the dynamic import is the only entry
 * point). Same DSN/project as the API + worker; the `tier` tag below
 * separates SPA-side errors from server-side ones in the Sentry UI.
 *
 * Release is stamped from VITE_COMMIT_SHA so a Sentry event links back
 * to the exact commit that shipped the regression. The deploy-bot
 * injects both env vars at SPA build time (see deploy_bot.py
 * PAGES_BUILD_ENV).
 */

let initialised = false;

export async function initSentry(): Promise<void> {
  if (initialised) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn || !dsn.trim()) return;

  try {
    // Dynamic import: keeps the SDK out of the bundle when DSN is unset,
    // and avoids blocking the first paint on Sentry's network init.
    const Sentry = await import("@sentry/react");
    Sentry.init({
      dsn,
      release: (import.meta.env.VITE_COMMIT_SHA as string | undefined) || undefined,
      environment: (import.meta.env.VITE_ENVIRONMENT as string | undefined) || "prod",
      // Conservative defaults — paid tier features (replays, perf
      // traces) opt-in via env. Errors at 100%.
      tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "0"),
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      sendDefaultPii: false,
      beforeSend(event) {
        // Drop known noisy non-bugs:
        //  - "Network request failed" / aborted fetches when the user
        //    navigates away mid-request. Real network issues still
        //    surface via the upper-tier (API 5xx logged server-side).
        //  - ResizeObserver loop warnings (browser-internal, not bugs).
        const msg = event.exception?.values?.[0]?.value ?? event.message ?? "";
        if (/ResizeObserver loop|Load failed|aborted/i.test(msg)) return null;
        return event;
      },
    });
    Sentry.setTag("tier", "spa");
    initialised = true;
  } catch (e) {
    // Don't crash the app if @sentry/react fails to load. Log to console
    // so a developer running locally can see the failure mode.
    // eslint-disable-next-line no-console
    console.warn("[sentry] init failed", e);
  }
}

/** Stamp the current scope with the signed-in user + tenant. Called from
 *  the auth layer once a session resolves. No-op when Sentry isn't on. */
export async function setSentryUser(opts: {
  userId?: string;
  tenantId?: string;
  email?: string;
}): Promise<void> {
  if (!initialised) return;
  try {
    const Sentry = await import("@sentry/react");
    if (opts.userId || opts.email) {
      Sentry.setUser({
        id: opts.userId,
        email: opts.email,
      });
    }
    if (opts.tenantId) {
      Sentry.setTag("tenant_id", opts.tenantId);
    }
  } catch {
    /* dynamic import failed; ignore — same fallback as init */
  }
}

/** Clear identity on sign-out so subsequent errors aren't attributed to
 *  the previous user. */
export async function clearSentryUser(): Promise<void> {
  if (!initialised) return;
  try {
    const Sentry = await import("@sentry/react");
    Sentry.setUser(null);
  } catch {
    /* ignore */
  }
}
